import React, { createContext, useContext, useState, useEffect } from 'react';
import { CartItem, Product, FlavorType, CustomerDetails, Order } from '../types/store';
import { STORE_CONFIG } from '../config/storeConfig';
import { PRODUCTS } from '../data/products';
import {
  createOrderDb,
  getLocalCachedOrders,
  subscribeOrdersDb,
  getStoreSettingsDb,
} from '../lib/firebase';
import { trackAddToCart, trackPurchase } from '../lib/analytics';

interface CartContextType {
  items: CartItem[];
  isCartOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
  addToCart: (product: Product, flavor?: FlavorType, quantity?: number) => void;
  buyNow: (product: Product, flavor?: FlavorType, quantity?: number) => void;
  removeFromCart: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  updateItemFlavor: (itemId: string, newFlavor: FlavorType) => void;
  clearCart: () => void;
  subtotal: number;
  shippingFee: number;
  remainingForFreeShipping: number;
  hasFreeShipping: boolean;
  totalItemsCount: number;
  total: number;
  discount: number;
  activePromoCode: string | null;
  applyPromoCode: (code: string) => { success: boolean; message: string };
  removePromoCode: () => void;
  orders: Order[];
  placeOrder: (customer: CustomerDetails) => Promise<Order>;
  getOrder: (orderNumber: string) => Order | undefined;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_STORAGE_KEY = 'healthstore_cart_v2';

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const saved = localStorage.getItem(CART_STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [orders, setOrders] = useState<Order[]>(() => getLocalCachedOrders());
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [activePromoCode, setActivePromoCode] = useState<string | null>(null);
  const [discountPercent, setDiscountPercent] = useState<number>(0);
  const [freeShippingThreshold, setFreeShippingThreshold] = useState<number>(STORE_CONFIG.freeShippingThreshold);
  const [standardShippingFee, setStandardShippingFee] = useState<number>(STORE_CONFIG.standardShippingFee);

  // Sync cart to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      console.error('Failed to save cart to localStorage', e);
    }
  }, [items]);

  // Subscribe to real orders from Firestore
  useEffect(() => {
    const unsub = subscribeOrdersDb((firestoreOrders) => {
      setOrders(firestoreOrders);
    });

    getStoreSettingsDb().then((settings) => {
      if (settings.freeShippingThreshold) setFreeShippingThreshold(settings.freeShippingThreshold);
      if (settings.standardShippingFee !== undefined) setStandardShippingFee(settings.standardShippingFee);
    });

    return () => unsub();
  }, []);

  const openCart = () => setIsCartOpen(true);
  const closeCart = () => setIsCartOpen(false);
  const toggleCart = () => setIsCartOpen((prev) => !prev);

  const addToCart = (product: Product, chosenFlavor?: FlavorType, quantity: number = 1) => {
    const flavor = chosenFlavor || product.flavor || 'Vanilla';
    const itemId = `${product.id}-${flavor}`;

    const variant = product.variants?.find((v) => v.flavor === flavor);
    const price = variant ? variant.price : product.price;
    const compareAtPrice = variant ? variant.compareAtPrice : product.compareAtPrice;
    const image = variant ? variant.image : (product.images && product.images[0] ? product.images[0] : '');
    const weight = variant ? variant.weight : product.weight;
    const title = variant ? variant.title : product.title;

    setItems((prevItems) => {
      const existingIndex = prevItems.findIndex((item) => item.id === itemId);
      if (existingIndex > -1) {
        const updated = [...prevItems];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + quantity,
        };
        return updated;
      }
      return [
        ...prevItems,
        {
          id: itemId,
          productId: product.id,
          title,
          flavor,
          weight,
          price,
          compareAtPrice,
          image,
          quantity,
        },
      ];
    });

    trackAddToCart(product, quantity);
    setIsCartOpen(true);
  };

  const buyNow = (product: Product, chosenFlavor?: FlavorType, quantity: number = 1) => {
    const flavor = chosenFlavor || product.flavor || 'Vanilla';
    const itemId = `${product.id}-${flavor}`;

    const variant = product.variants?.find((v) => v.flavor === flavor);
    const price = variant ? variant.price : product.price;
    const compareAtPrice = variant ? variant.compareAtPrice : product.compareAtPrice;
    const image = variant ? variant.image : (product.images && product.images[0] ? product.images[0] : '');
    const weight = variant ? variant.weight : product.weight;
    const title = variant ? variant.title : product.title;

    // Set cart to strictly this item for instant checkout
    setItems([
      {
        id: itemId,
        productId: product.id,
        title,
        flavor,
        weight,
        price,
        compareAtPrice,
        image,
        quantity,
      },
    ]);

    trackAddToCart(product, quantity);
    setIsCartOpen(false);
  };

  const removeFromCart = (itemId: string) => {
    setItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  const updateQuantity = (itemId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(itemId);
      return;
    }
    setItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, quantity } : item))
    );
  };

  const updateItemFlavor = (itemId: string, newFlavor: FlavorType) => {
    setItems((prev) => {
      const itemToUpdate = prev.find((item) => item.id === itemId);
      if (!itemToUpdate) return prev;

      const product = PRODUCTS.find((p) => p.id === itemToUpdate.productId);
      if (!product) return prev;

      const variant = product.variants?.find((v) => v.flavor === newFlavor);
      const newPrice = variant ? variant.price : itemToUpdate.price;
      const newCompare = variant ? variant.compareAtPrice : itemToUpdate.compareAtPrice;
      const newImg = variant ? variant.image : itemToUpdate.image;
      const newTitle = variant ? variant.title : itemToUpdate.title;
      const newId = `${product.id}-${newFlavor}`;

      const filtered = prev.filter((item) => item.id !== itemId);
      const existingNewItem = filtered.find((item) => item.id === newId);

      if (existingNewItem) {
        return filtered.map((item) =>
          item.id === newId
            ? { ...item, quantity: item.quantity + itemToUpdate.quantity }
            : item
        );
      }

      return [
        ...filtered,
        {
          ...itemToUpdate,
          id: newId,
          title: newTitle,
          flavor: newFlavor,
          price: newPrice,
          compareAtPrice: newCompare,
          image: newImg,
        },
      ];
    });
  };

  const clearCart = () => {
    setItems([]);
    setActivePromoCode(null);
    setDiscountPercent(0);
  };

  const subtotal = items.reduce((acc, item) => acc + item.price * item.quantity, 0);
  const totalItemsCount = items.reduce((acc, item) => acc + item.quantity, 0);

  const hasFreeShipping = subtotal >= freeShippingThreshold;
  const remainingForFreeShipping = Math.max(0, freeShippingThreshold - subtotal);
  const shippingFee = items.length === 0 ? 0 : hasFreeShipping ? 0 : standardShippingFee;

  const discount = Math.round((subtotal * discountPercent) / 100);
  const total = Math.max(0, subtotal - discount + shippingFee);

  const applyPromoCode = (code: string) => {
    const cleanCode = code.trim().toUpperCase();
    if (cleanCode === 'HEALTH10' || cleanCode === 'WELCOME10') {
      setActivePromoCode(cleanCode);
      setDiscountPercent(10);
      return { success: true, message: '🎉 Promo code applied: 10% OFF your order!' };
    }
    if (cleanCode === 'MALRICH5' || cleanCode === 'HEALTH5') {
      setActivePromoCode(cleanCode);
      setDiscountPercent(5);
      return { success: true, message: '🎉 Promo code applied: 5% OFF your order!' };
    }
    return { success: false, message: 'Invalid promo code. Try "HEALTH10"' };
  };

  const removePromoCode = () => {
    setActivePromoCode(null);
    setDiscountPercent(0);
  };

  const placeOrder = async (customer: CustomerDetails): Promise<Order> => {
    const calculatedDiscount = discount;
    const newOrder = await createOrderDb(customer, items, calculatedDiscount);

    // Track e-commerce conversion event
    trackPurchase(newOrder);

    // Update local state and clear cart
    setOrders((prev) => [newOrder, ...prev.filter((o) => o.id !== newOrder.id)]);
    clearCart();
    return newOrder;
  };

  const getOrder = (orderNumber: string): Order | undefined => {
    const cleanSearch = orderNumber.trim().toUpperCase();
    return orders.find(
      (o) =>
        o.orderNumber.toUpperCase() === cleanSearch ||
        o.trackingNumber?.toUpperCase() === cleanSearch ||
        o.id.toUpperCase() === cleanSearch
    );
  };

  return (
    <CartContext.Provider
      value={{
        items,
        isCartOpen,
        openCart,
        closeCart,
        toggleCart,
        addToCart,
        buyNow,
        removeFromCart,
        updateQuantity,
        updateItemFlavor,
        clearCart,
        subtotal,
        shippingFee,
        remainingForFreeShipping,
        hasFreeShipping,
        totalItemsCount,
        total,
        discount,
        activePromoCode,
        applyPromoCode,
        removePromoCode,
        orders,
        placeOrder,
        getOrder,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
